import { User } from '@aws-sdk/client-iam'
import chalk from 'chalk'
import inquirer from 'inquirer'
import { obfuscateAnswer } from '../transformers'
import { AwsAccessAnswers, AwsRegionAnswers, AwsS3AndIAMUserQuestions, ExtendedQuestion } from './types'
import { Utils } from './utils'
import { isValidAccessKeyId, isValidIAMUserName, isValidS3BucketName } from './validators'

const awsAccessKeyId = process.env.AWS_ACCESS_KEY_ID
const awsSecretAccessKey = process.env.AWS_SECRET_ACCESS_KEY
const awsDefaultRegion = process.env.AWS_DEFAULT_REGION

const printMessageToScreen = (message: string) => process.stdout.write(message)
const askQuestions = inquirer.prompt

const awsAccessQuestions: ExtendedQuestion[] = [
  {
    type: 'input',
    name: 'awsAccessKeyId',
    message: `Please enter the AWS Access Key that you want to use to create the match resources on AWS \n ${chalk.underline.green(
      '(e.g. AKIAIOSFODNN7EXAMPLE)',
    )}:`,
    ...(awsSecretAccessKey && { default: awsAccessKeyId }),
    transformer: obfuscateAnswer,
    validate: isValidAccessKeyId,
  },
  {
    type: 'input',
    name: 'awsSecretAccessKey',
    message: `Please enter the Secret AWS Access Key that you want to use to create the match resources on AWS \n ${chalk.underline.green(
      '(e.g. wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY)',
    )}:`,
    ...(awsSecretAccessKey && { default: awsSecretAccessKey }),
    transformer: obfuscateAnswer,
  },
]

const awsRegionQuestions = (regions: string[]) => [
  {
    type: 'list',
    name: 'awsRegion',
    choices: regions,
    message: `Please choose the AWS region that you want to use to create the S3 bucket in.`,
    ...(awsDefaultRegion && { default: awsDefaultRegion }),
  },
]

const s3AndIAMUserQuestions = [
  {
    type: 'input',
    name: 'awsIAMUserName',
    message: `Please enter a valid IAM user name to create for read access to your match s3 bucket\n${chalk.underline.green(
      '(e.g. fastlane-match-s3-app-name)',
    )}:`,
    validate: isValidIAMUserName,
  },
  {
    type: 'input',
    name: 'awsS3BucketName',
    message: `Please enter S3 bucket name to create. This should be unique globally and conform to the S3 bucket naming conventions\n${chalk.underline.green(
      '(e.g. fastlane-match-s3-app-name)',
    )}: `,
    validate: isValidS3BucketName,
  },
]

const createUtils = async (): Promise<Utils> => {
  const awsAccessAnswers: AwsAccessAnswers = (await askQuestions(awsAccessQuestions)) as AwsAccessAnswers

  const utils = new Utils({
    credentials: {
      accessKeyId: awsAccessAnswers.awsAccessKeyId,
      secretAccessKey: awsAccessAnswers.awsSecretAccessKey,
    },
  })

  const regionList = await utils.getRegions()

  const awsRegionAnswers = (await askQuestions(awsRegionQuestions(regionList))) as AwsRegionAnswers

  utils.setRegion(awsRegionAnswers.awsRegion)

  return utils
}

const IAMUserExists = async (username: string, utils: Utils): Promise<boolean> => {
  try {
    const awsUser = await utils.getUser(username)
    if (awsUser) {
      return true
    }
    return false
  } catch (error) {
    return false
  }
}

const printMatchResourceDetailsToScreen = (
  IAMUserName: string,
  AccessKeyId: string | undefined,
  SecretAccessKey: string | undefined,
  S3BucketName: string | undefined,
): void => {
  const resourceDetails = [
    `\nComplete, please note down the following details which you will use with your CI config and locally:\n\n`,
    `${chalk.yellow('IAM Username: ')}`,
    IAMUserName,
    `\n${chalk.yellow('Access Key: ')}`,
    AccessKeyId,
    `\n${chalk.yellow('Secret Access Key: ')}`,
    SecretAccessKey,
    `\n${chalk.yellow('Match S3 Bucket Name: ')}`,
    S3BucketName,
    `\n`,
  ]

  resourceDetails.forEach(message => {
    printMessageToScreen(message ?? '')
  })
}

const createMatchResources = async (): Promise<void> => {
  try {
    printMessageToScreen(
      `${chalk.red(
        'Starting the creation of AWS resources for your Match Certificates.',
      )}\nAny answers listed in gray are defaults that are already set and can be chosen by entering nothing.\n`,
    )

    const utils = await createUtils()

    const s3AndIAMUserAnswers = (await askQuestions(s3AndIAMUserQuestions)) as AwsS3AndIAMUserQuestions

    const awsUserExists = await IAMUserExists(s3AndIAMUserAnswers.awsIAMUserName, utils)

    if (awsUserExists) {
      printMessageToScreen(
        `\n${chalk.red(
          'An IAM user already exists with that name, user cannot already exist in your account, exiting.',
        )}\n`,
      )
      throw new Error('IAM user already exists with that username, user cannot already exist in your account.')
    }

    printMessageToScreen(`\n\nCreating Match S3 bucket....`)
    await utils.createS3Bucket(s3AndIAMUserAnswers.awsS3BucketName)
    printMessageToScreen(`${chalk.green('done.')}\n`)

    printMessageToScreen('Creating a new IAM user...')
    const awsUser = await utils.createUser(s3AndIAMUserAnswers.awsIAMUserName)
    printMessageToScreen(`${chalk.green('done.')}\n`)

    printMessageToScreen('Creating a new Access Key Pair for the user...')
    const accessKey = await utils.createAccessKey(s3AndIAMUserAnswers.awsIAMUserName)
    printMessageToScreen(`${chalk.green('done.')}\n`)

    printMessageToScreen(`Granting the user read permissions on the bucket...`)
    await utils.grantBucketReadAccess(awsUser as User, s3AndIAMUserAnswers.awsS3BucketName)
    printMessageToScreen(`${chalk.green('done.')}\n`)

    printMatchResourceDetailsToScreen(
      s3AndIAMUserAnswers.awsIAMUserName,
      accessKey?.AccessKeyId,
      accessKey?.SecretAccessKey,
      s3AndIAMUserAnswers.awsS3BucketName,
    )
  } catch (error) {
    console.error('Error:', error?.message ?? error)
  }
}

export const command = 'creatematchresources'
export const desc =
  'Create an S3 bucket and IAM user with read access to that bucket, allowing safe storage of your Match certificates'
export const handler = createMatchResources
